import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { submitInternReport, getReportsByEmployee } from "../../http";

const InternReport = () => {
  const { user } = useSelector((state) => state.authSlice);
  const [reports, setReports] = useState([
    { task: "", status: "", remark: "" },
  ]);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [userReports, setUserReports] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUserReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserReports = async () => {
    try {
      setLoading(true);
      const response = await getReportsByEmployee(user.id);
      //console.log("Fetch reports response:", response);

      if (response && Array.isArray(response)) {
        setUserReports(response);
      } else if (response && Array.isArray(response.data)) {
        setUserReports(response.data);
      } else {
        console.error("Unexpected response format:", response);
        setUserReports([]);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      setUserReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    setReports([...reports, { task: "", status: "", remark: "" }]);
  };

  const handleDeleteRow = (index) => {
    // Prevent deleting the last row
    if (reports.length === 1) {
      toast.warning("Cannot delete the last row");
      return;
    }

    const newReports = reports.filter((_, i) => i !== index);
    setReports(newReports);
  };

  const handleChange = (index, field, value) => {
    const newReports = [...reports];
    newReports[index][field] = value;
    setReports(newReports);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate if any field is empty
    const hasEmptyFields = reports.some(
      (report) => !report.task || !report.status || !report.remark
    );

    if (hasEmptyFields) {
      toast.error("Please fill all fields");
      return;
    }

    // Create the data object that would be sent to backend
    const reportData = {
      employeeId: user.id,
      employeeName: user.name,
      date: date,
      reports: reports,
      teamId: user.teamId,
      submittedAt: new Date().toISOString(),
    };

    try {
      const response = await submitInternReport(reportData);
      //console.log("Submit response:", response);

      if (response) {
        toast.success("Report submitted successfully");
        setReports([{ task: "", status: "", remark: "" }]);
        await fetchUserReports();
      } else {
        toast.error("Failed to submit report");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit report");
    }
  };

  return (
    <div className="main-content">
      <section className="section">
        <div className="card">
          <div className="card-header">
            <h4>Daily Report</h4>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                className="form-control mb-3"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Status by EOD</th>
                    <th>Remark</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={report.task}
                          onChange={(e) =>
                            handleChange(index, "task", e.target.value)
                          }
                          placeholder="Enter task"
                        />
                      </td>
                      <td>
                        <select
                          className="form-control"
                          value={report.status}
                          onChange={(e) =>
                            handleChange(index, "status", e.target.value)
                          }>
                          <option value="">Select Status</option>
                          <option value="Completed">Completed</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Pending">Pending</option>
                          <option value="Blocked">Blocked</option>
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={report.remark}
                          onChange={(e) =>
                            handleChange(index, "remark", e.target.value)
                          }
                          placeholder="Enter remark"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteRow(index)}
                          title="Delete row">
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-between mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddRow}>
                Add Row
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmit}>
                Submit Report
              </button>
            </div>
          </div>
        </div>

        {/* New Reports History Card */}
        <div className="card mt-4">
          <div className="card-header">
            <h4>Reports History</h4>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : userReports.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Tasks</th>
                      <th>Status</th>
                      <th>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userReports.map((report, index) => (
                      <tr key={index}>
                        <td>{new Date(report.date).toLocaleDateString()}</td>
                        <td>
                          <ul>
                            {report.reports.map((r, i) => (
                              <li key={i}>{r.task}</li>
                            ))}
                          </ul>
                        </td>
                        <td>
                          <ul>
                            {report.reports.map((r, i) => (
                              <li key={i}>{r.status}</li>
                            ))}
                          </ul>
                        </td>
                        <td>
                          <ul>
                            {report.reports.map((r, i) => (
                              <li key={i}>{r.remark}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center">No reports found</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default InternReport;
